import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  title: string;
  subtitle?: string;
  illustration?: ReactNode;
  footer?: ReactNode;
}

/**
 * Two-column layout shared by every auth page. Form on the left
 * (so the focus lands there); brand illustration on the right
 * (collapses on mobile so the form takes the viewport).
 */
export function AuthLayout({ children, title, subtitle, illustration, footer }: Props) {
  const { t } = useTranslation();
  return (
    <div className="relative isolate min-h-screen overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 gradient-mesh" />
        <motion.div
          className="absolute -top-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-gradient-to-br from-accent-500/35 to-fuchsia-400/30 blur-3xl"
          animate={{ y: [0, -20, 0], x: [0, 15, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-gradient-to-br from-violet-300/30 to-accent-400/30 blur-3xl"
          animate={{ y: [0, 20, 0], x: [0, -15, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <Link
        to="/"
        className="absolute top-5 left-5 z-10 inline-flex items-center gap-2 rounded-full glass-strong px-3 py-1.5 shadow-soft hover:shadow-glow transition"
      >
        <BrandMark />
        <span className="text-sm font-semibold tracking-tight serif-jp">{t('brand.name')}</span>
      </Link>

      <div className="relative grid min-h-screen lg:grid-cols-2">
        <div className="flex items-center justify-center px-4 py-20 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 0.84, 0.44, 1] }}
            className="w-full max-w-md"
          >
            <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight text-balance text-ink-900">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-2 text-sm text-ink-600 text-pretty">{subtitle}</p>
            )}
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-6 text-sm text-ink-600">{footer}</div>}
          </motion.div>
        </div>

        <div className="hidden lg:flex relative items-center justify-center px-8 py-12 border-l border-ink-200/40 bg-white/30 backdrop-blur-sm">
          {illustration ?? <DefaultIllustration />}
        </div>
      </div>
    </div>
  );
}

function BrandMark() {
  // Match the marketing SiteHeader's canonical logo markup (28px, rounded-md,
  // decorative alt — the adjacent brand-name span provides the accessible name).
  return <img src="/logo.png" alt="" width={28} height={28} className="rounded-md object-contain shrink-0" />;
}

function DefaultIllustration() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full max-w-md">
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        className="rounded-3xl glass-strong shadow-card-lift p-6"
      >
        <p className="text-xs uppercase tracking-[0.2em] text-accent-700 font-semibold">
          {t('hero.tag')}
        </p>
        <p className="display serif-jp mt-2 text-2xl font-semibold leading-tight text-ink-900">
          {t('brand.tagline')}
        </p>
        <div className="mt-5 grid grid-cols-3 gap-2 text-center">
          {[
            { v: '14', l: t('auth.stat_languages') },
            { v: '6', l: t('auth.stat_agents') },
            { v: '15m', l: t('auth.stat_time_to_first_signal') },
          ].map((s) => (
            <div key={s.l} className="rounded-xl bg-ink-100/70 px-2 py-3">
              <p className="num text-xl font-bold gradient-text">{s.v}</p>
              <p className="text-[10px] uppercase tracking-wide text-ink-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0], rotate: [-2, -2, -2] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -left-6 -bottom-6 rounded-2xl glass-strong shadow-soft px-4 py-3 flex items-center gap-3"
      >
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-accent-500 to-fuchsia-500 text-white">
          ✦
        </span>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-ink-500">{t('auth.illus_signal')}</p>
          <p className="text-xs font-semibold text-ink-900">¥1.2B Series B</p>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, -8, 0], rotate: [2, 2, 2] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
        className="absolute -right-6 -top-6 rounded-2xl glass-strong shadow-soft px-4 py-3 flex items-center gap-2"
      >
        <span className="text-base">🇯🇵</span>
        <span className="text-xs font-semibold text-ink-900">{t('auth.illus_keigo')}</span>
      </motion.div>
    </div>
  );
}
